<template>
    <div class="custom-text-input-container" :class="{ 'mt-0': withoutMarginTop, block: block, 'read-only': readOnly, small: small }">
        <div class="input-button-container">
            <div class="custom-text-input" :class="{ 'is-textarea': textarea, highlight: highlight }">
                <div v-if="type == 'password'" class="toggle-password-visibility" @click="showPassword = !showPassword">
                    <icon name="eye" scale="1.1" v-if="showPassword" />
                    <icon name="eye-slash" scale="1.1" v-else />
                </div>
                <b-form-input
                    v-if="!textarea"
                    ref="input-text"
                    @input="input"
                    @change="change"
                    @update="update"
                    @blur="blur"
                    @keyup.native.enter="enter"
                    :name="name"
                    :placeholder="placeholder"
                    :type="inputType"
                    :value="value"
                    :autofocus="autofocus"
                    :disabled="disabled"
                    :readonly="readOnly"
                    :required="required"
                    :maxlength="maxlength"
                    :class="{
                        'fit-button': rightButton,
                        'pr-5': attachment,
                        blue: variant == 'blue',
                        light: variant == 'light',
                        dark: variant == 'dark',
                        error: error,
                        password: type == 'password',
                    }"
                />
                <b-form-textarea
                    v-else
                    ref="input-text"
                    @input="input"
                    @change="change"
                    @update="update"
                    @blur="blur"
                    @keyup.native.enter="enter"
                    :name="name"
                    :placeholder="placeholder"
                    :type="type"
                    :value="value"
                    :autofocus="autofocus"
                    :disabled="disabled || (rightButton && rightButton.loading)"
                    :required="required"
                    :maxlength="maxlength"
                    :rows="textAreaRows"
                    no-auto-shrink
                    no-resize
                    :class="{
                        'fit-button': rightButton,
                        'pr-5': attachment,
                        blue: variant == 'blue',
                        light: variant == 'light',
                        dark: variant == 'dark',
                        error: error,
                        password: type == 'password',
                    }"
                ></b-form-textarea>
                <label>
                    <span v-if="required && !readOnly">*</span>
                    {{ label }}
                </label>
                <div @click="triggerAttachmentInput" v-if="attachment" class="attachment d-flex h-100 align-items-center">
                    <icon name="paperclip" scale="1" color="var(--secondary-3)" />
                </div>
            </div>

            <transition name="slide">
                <ci-button
                    v-if="rightButton"
                    @click="
                        ($e) => {
                            $emit('rightButtonClick', $e)
                        }
                    "
                    fitInput
                    :capitalCase="rightButton.capitalCase"
                    :loading="rightButton.loading"
                    :text="rightButton.text"
                    :centerIcon="rightButton.centerIcon"
                    :disabled="error || rightButton.disabled"
                    :type="rightButton.type"
                />
            </transition>
        </div>
        <p v-if="error" class="custom-error-text">{{ validatorErrorMessage }}</p>
    </div>
</template>
<script>
import 'vue-awesome/icons/eye-slash'
import 'vue-awesome/icons/eye'
import 'vue-awesome/icons/paperclip'

export default {
    /**
     * Summary.
     * Use this component to mount a custom CIN input.
     *
     * @param {String} label - The input Label
     * @param {String} name - The input name
     * @param {String} placeholder - The input placeholder
     * @param {String} type - The input type. Possible values: text | number | password
     * @param {String} value - The input initial value
     * @param {String} validatorErrorMessage - The error message to show when the @validator Function fails
     * @param {String} variant - The input variant color. Possible values: light | blue | dark | gray
     * @param {String} maxlength - The max length of the input
     *
     * @param {Boolean} attachment - If the input needs an attachment button
     * @param {Boolean} small - Forces input to fit 32px height
     * @param {Boolean} block - If it need to be 100% width
     * @param {Boolean} withoutMarginTop - If the input doesn't need margin top
     * @param {Boolean} autofocus - If the input needs to focus when it shows
     * @param {Boolean} disabled - If the input is disabled
     * @param {Boolean} readOnly - If the input is readOnly
     * @param {Boolean} required - If it's required
     * @param {Boolean} textarea - If the input type is textarea
     * @param {Boolean} highlight - turns the border blue
     *
     * @param {Object} rightButton - An object containing the ci-button component attributes.
     *
     * @param {Number} validationDebouce - The value (in ms) to execute the @validator Function
     * @param {Number} textAreaRows - Textarea rows size
     *
     * @param {Function} validator - A function that returns a @Boolean to validate the input content.
     * @param {Function} formatter - A function to format the input content
     *
     * @event v-model/input - Emitted when user types
     * @event change - Emitted when input changes (fired after @input event)
     * @event update - Emitted when the values change
     * @event blur - Emitted after the input looses focus
     * @event enter - Emitted when user press the Enter key.
     * @event rightButtonClick - Emitted when the right button is clicked
     * @event openFileInput - Emitted when user clicks attachment icon (depends on attachament prop = true to be called)
     */
    name: 'CustomTextInput',
    props: {
        label: String,
        name: String,
        placeholder: String,
        type: String,
        value: null,
        validatorErrorMessage: {
            type: String,
            default: 'O valor do campo é inválido!',
        },
        variant: {
            type: String,
            default: 'blue',
        },
        maxlength: {
            type: String,
        },

        attachment: Boolean,
        small: Boolean,
        block: Boolean,
        withoutMarginTop: Boolean,
        autofocus: Boolean,
        disabled: Boolean,
        readOnly: {
            type: Boolean,
            default: false,
        },
        required: Boolean,
        textarea: Boolean,
        highlight: {
            type: Boolean,
            default: false,
        },

        rightButton: Object,

        validationDebouce: {
            type: Number,
            default: 0,
        },
        textAreaRows: {
            type: Number,
            default: 2,
        },

        validator: {
            type: Function,
            default: (value) => {
                return true
            },
        },
        formatter: {
            type: Function,
            default: (value) => {
                return value
            },
        },
    },
    computed: {
        inputType: function () {
            if (this.type == 'password') {
                return this.showPassword ? 'text' : 'password'
            } else return this.type
        },
    },
    data() {
        return {
            attachments: [],
            error: false,
            showPassword: false,
        }
    },
    methods: {
        /**
         * Summary.
         * The next 5 methods are used to emit the input basic events
         */
        input(value) {
            let formattedValue = this.formatter(value)
            this.$emit('input', formattedValue)
            this.validate(formattedValue)
        },
        change(value) {
            this.$emit('change', value)
        },
        update(value) {
            this.$emit('update', value)
        },
        blur() {
            this.$emit('blur')
        },
        enter() {
            if (!this.error) this.$emit('enter')
        },

        /**
         * Summary.
         * this method validates the input content based on the @param {Function} validator
         *
         * @param {String} value - The current input value
         */
        validate(value) {
            setTimeout(() => {
                this.error = !this.validator(value)
            }, this.validationDebouce)
        },

        /**
         * Summary
         * This method triggers the file input
         */
        triggerAttachmentInput() {
            // this.$refs['attachment-input'].$el.children[0].click()
            this.$emit('openFileInput')
        },

        /**
         * Summary
         * Activate input focus
         */
        focus() {
            this.$refs['input-text'].focus()
        },
        unfocus() {
            this.$refs['input-text'].blur()
        },
    },
    mounted() {
        if (this.value) {
            this.error = this.validate(this.value)
        }
    },
}
</script>
<style lang="scss" scoped>
@import '@/scss/components/Utils/custom-text-input.scss';
</style>
